import type { ReactNode } from 'react';
import {
  Bug,
  Check,
  ChevronDown,
  ChevronUp,
  ChevronsUp,
  CircleCheck,
  CircleSlash,
  Clock,
  Copy,
  Lightbulb,
  Minus,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import {
  SUPPORT_PRIORITY_LABELS,
  SUPPORT_TICKET_KIND_LABELS,
  type SupportArea,
  type SupportPriority,
  type SupportStatus,
  type SupportTicketKind,
} from '@asyagrup/group-hub-contracts';
import { ApiError, errorMessage } from '@/lib/api/client';
import { Button, Card, Td, cx } from '@/ui/primitives';

/**
 * `Tone` is not exported from the primitives, so a feature that needs to type a
 * status→tone map has to restate the union. Restating it here rather than
 * widening to `string` keeps a typo in the map a compile error.
 */
type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'primary';

export const STATUS_TONE: Record<SupportStatus, Tone> = {
  OPEN: 'warning',
  IN_PROGRESS: 'primary',
  RESOLVED: 'success',
  CLOSED: 'neutral',
  REJECTED: 'danger',
  DUPLICATE: 'neutral',
};

export const STATUS_ICON: Record<SupportStatus, LucideIcon> = {
  OPEN: Clock,
  IN_PROGRESS: Wrench,
  RESOLVED: CircleCheck,
  CLOSED: Check,
  REJECTED: CircleSlash,
  DUPLICATE: Copy,
};

/**
 * Tone utilities land on glyphs, never on the sentence beside them. SideNav
 * measured `text-warning` at small sizes as 3.23:1 in light mode — a WCAG 1.4.3
 * failure — so status colour lives on the icon and inside `Badge`, and the
 * prose stays `text-fg` / `text-fg-muted`.
 */
const STATUS_GLYPH: Record<SupportStatus, string> = {
  OPEN: 'text-warning',
  IN_PROGRESS: 'text-primary',
  RESOLVED: 'text-success',
  CLOSED: 'text-fg-muted',
  REJECTED: 'text-danger',
  DUPLICATE: 'text-fg-muted',
};

/**
 * Priority is ranked by shape before it is ranked by colour: ⌃⌃ → ⌃ → − → ⌄.
 * Deuteranopes confuse this repo's danger red with its warning amber, and the
 * chevrons stay monotonic when the hue disappears.
 */
const PRIORITY_MARK: Record<SupportPriority, { Icon: LucideIcon; glyph: string }> = {
  LOW: { Icon: ChevronDown, glyph: 'text-fg-muted' },
  NORMAL: { Icon: Minus, glyph: 'text-fg-muted' },
  HIGH: { Icon: ChevronUp, glyph: 'text-warning' },
  CRITICAL: { Icon: ChevronsUp, glyph: 'text-danger' },
};

/** Phone cards have no priority column, so the rank moves to a left edge rail. */
export const PRIORITY_RAIL: Record<SupportPriority, string> = {
  LOW: 'bg-border',
  NORMAL: 'bg-border',
  HIGH: 'bg-warning',
  CRITICAL: 'bg-danger',
};

/**
 * The status vocabulary is the support team's; these are the same six states
 * said back to the person who filed the report. "Çözüldü" is a fact about the
 * queue, "Çözüldü — onayınız bekleniyor" is the thing they have to act on.
 */
export const STATUS_SENTENCE_REPORTER: Record<SupportStatus, string> = {
  OPEN: 'Alındı, sıraya girdi',
  IN_PROGRESS: 'Üzerinde çalışılıyor',
  RESOLVED: 'Çözüldü — onayınız bekleniyor',
  CLOSED: 'Kapandı',
  REJECTED: 'İşleme alınmadı — gerekçe kayıtta',
  DUPLICATE: 'Aynı konu başka bir kayıtta takip ediliyor',
};

export const STATUS_EXPLAIN: Record<SupportStatus, string> = {
  OPEN: 'Kaydınız destek ekibine ulaştı. Sıraya alındı; biri bakmaya başladığında burada göreceksiniz.',
  IN_PROGRESS: 'Bir destek görevlisi kaydınızı üstlendi. Soru çıkarsa aşağıdan yazacak.',
  RESOLVED: 'Sorunun giderildiği bildirildi. Kontrol edip onaylayın; hâlâ sürüyorsa aşağıya yazın.',
  CLOSED: 'Bu kayıt kapandı. Aynı sorunu yeniden yaşarsanız yeni bir kayıt açın.',
  REJECTED: 'Bu kayıt işleme alınmadı. Gerekçe aşağıdaki yazışmada.',
  DUPLICATE: 'Aynı konu başka bir kayıtta takip ediliyor. Gerekçedeki numaradan izleyebilirsiniz.',
};

/** The banner carries severity in border + tint; the sentence inside stays `text-fg`. */
export const STATUS_BANNER: Record<SupportStatus, string> = {
  OPEN: 'border-warning/30 bg-warning/10',
  IN_PROGRESS: 'border-primary/30 bg-primary/10',
  RESOLVED: 'border-success/30 bg-success/10',
  CLOSED: 'border-border bg-surface-muted/40',
  DUPLICATE: 'border-border bg-surface-muted/40',
  REJECTED: 'border-danger/30 bg-danger/10',
};

/**
 * Buttons are verbs. `SUPPORT_STATUS_LABELS` names a state, so a button reading
 * "Kapandı" asks the reader to press a fact — worst for the reporter, whose one
 * allowed transition is the whole point of the two-state design.
 */
export const STATUS_ACTION_LABELS: Record<SupportStatus, string> = {
  IN_PROGRESS: 'Üzerinde çalışmaya başla',
  RESOLVED: 'Çözüldü olarak işaretle',
  OPEN: 'Yeniden aç',
  CLOSED: 'Kaydı kapat',
  REJECTED: 'Reddet',
  DUPLICATE: 'Mükerrer olarak kapat',
};

/** Both need a reason and both are terminal — segregated from the constructive row. */
export const TERMINAL_DECISIONS = new Set<SupportStatus>(['REJECTED', 'DUPLICATE']);

/** Reached these and the record can never be reopened; only a new ticket follows. */
export const TERMINAL_STATUSES = new Set<SupportStatus>(['CLOSED', 'REJECTED', 'DUPLICATE']);

/**
 * Route prefix → area, so the compose sheet lands on the screen the reporter
 * came from. The server's default is OTHER, which is why most tickets today sit
 * in "Diğer" and the field's routing purpose is defeated on arrival.
 */
const AREA_BY_PATH: ReadonlyArray<[string, SupportArea]> = [
  ['/izin', 'LEAVE'],
  ['/puantaj', 'ATTENDANCE'],
  ['/odeme', 'PAYMENT'],
  ['/kasa', 'TREASURY'],
  ['/banka', 'TREASURY'],
  ['/misafirhane', 'KONAKLAMA'],
  ['/konaklama', 'KONAKLAMA'],
  ['/personel', 'HR'],
  ['/kullanici', 'AUTH'],
  ['/roller', 'AUTH'],
  ['/entegrasyon', 'INTEGRATION'],
  ['/sabit-gider', 'FINANCE'],
  ['/finans', 'FINANCE'],
];

export function areaFromPath(pathOrUrl: string | null): SupportArea | '' {
  if (!pathOrUrl) return '';
  let path = pathOrUrl;
  try {
    path = new URL(pathOrUrl, window.location.origin).pathname;
  } catch {
    // A hand-typed value is not a URL yet; matching the raw text is still useful.
  }
  const hit = AREA_BY_PATH.find(([prefix]) => path.startsWith(prefix));
  return hit ? hit[1] : '';
}

// --- formatting --------------------------------------------------------------

/**
 * Copied rather than imported: `formatDateTime` lives in `features/fixed-expense/api.ts`
 * and no feature in this codebase reaches into another feature's api module.
 */
export function formatDateTime(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback;
  return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
}

/** There is no byte formatter anywhere in the repo; the 10 MB ceiling needs one. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return 'boyut bilinmiyor';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${trim(kb)} KB`;
  return `${trim(kb / 1024)} MB`;
}

function trim(value: number): string {
  return value.toFixed(value < 10 ? 1 : 0).replace('.', ',');
}

/**
 * Age is the primary triage signal in any queue and the screen had none.
 * `alarming` is a week, which is the point where "sıraya girdi" stops being a
 * true sentence — it is a display threshold, not a policy, so it lives here.
 */
export function ageOf(iso: string): { text: string; alarming: boolean } {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return { text: 'az önce', alarming: false };
  if (minutes < 60) return { text: `${minutes} dk`, alarming: false };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { text: `${hours} saat`, alarming: false };
  const days = Math.floor(hours / 24);
  return { text: `${days} gün`, alarming: days >= 7 };
}

export function ageText(iso: string): string {
  const age = ageOf(iso);
  return age.text === 'az önce' ? age.text : `${age.text} önce`;
}

/**
 * A 409 is not a failure, it is "someone else moved this while you were
 * reading". A 404 is not "silinmiş" either: out-of-scope identities 404 rather
 * than 403 by repo rule, so guessing deletion would be wrong more often.
 */
export function conflictAwareMessage(error: unknown): string {
  if (error instanceof ApiError && error.statusCode === 409)
    return 'Kayıt siz bakarken değişti. Sayfayı yenileyip tekrar deneyin.';
  if (error instanceof ApiError && error.statusCode === 404)
    return 'Bu kayıt yok ya da görme yetkiniz yok.';
  return errorMessage(error);
}

// --- marks -------------------------------------------------------------------

export function PriorityMark({
  priority,
  withLabel = false,
}: {
  priority: SupportPriority;
  withLabel?: boolean;
}) {
  const { Icon, glyph } = PRIORITY_MARK[priority];
  return (
    <span className="inline-flex items-center gap-1">
      <Icon size={14} className={cx('shrink-0', glyph)} aria-hidden />
      {withLabel ? (
        <span className="text-fg-muted text-xs">{SUPPORT_PRIORITY_LABELS[priority]}</span>
      ) : (
        <span className="sr-only">Önem: {SUPPORT_PRIORITY_LABELS[priority]}</span>
      )}
    </span>
  );
}

/**
 * Kind is silhouette-only — a beetle and a bulb differ in outline, so it never
 * needed colour, and the tile is identical for both. The word is always present
 * for assistive tech; today the icon is `aria-hidden` with no text equivalent.
 */
export function KindMark({ kind, size = 8 }: { kind: SupportTicketKind; size?: 7 | 8 | 9 }) {
  const Icon = kind === 'BUG' ? Bug : Lightbulb;
  const box = size === 7 ? 'size-7' : size === 9 ? 'size-9' : 'size-8';
  return (
    <span
      className={cx(
        'bg-surface-muted text-fg-muted flex shrink-0 items-center justify-center rounded-lg',
        box,
      )}
    >
      <Icon size={size === 7 ? 14 : 16} aria-hidden />
      <span className="sr-only">{SUPPORT_TICKET_KIND_LABELS[kind]}</span>
    </span>
  );
}

export function StatusMark({ status, size = 14 }: { status: SupportStatus; size?: number }) {
  const Icon = STATUS_ICON[status];
  return <Icon size={size} className={cx('mt-0.5 shrink-0', STATUS_GLYPH[status])} aria-hidden />;
}

// --- small shared pieces -----------------------------------------------------

/** The label/value pair `DataTable` uses on its phone cards, restated so the
 *  hand-written queue card is indistinguishable from the rest of the app. */
export function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-fg-muted shrink-0 text-xs">{label}</dt>
      <dd className="text-fg min-w-0 text-right text-sm">{children}</dd>
    </div>
  );
}

/**
 * The server defaults to 25 per page and the screen never sent `page`, so
 * ticket 26 and beyond were unreachable and the list simply ended.
 */
export function Pager({
  page,
  totalPages,
  total,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPage: (page: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="text-fg-muted mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
      <span>
        {total} kayıt · sayfa {page}/{totalPages}
      </span>
      <div className="flex gap-2">
        <Button size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Önceki
        </Button>
        <Button size="sm" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
          Sonraki
        </Button>
      </div>
    </div>
  );
}

/**
 * The list hooks carry no `placeholderData`, so a filter change really does
 * unmount the rows. Keeping the table shell and filling it with fixed-height
 * rows is what stops the filter bar jumping up the page and back.
 */
export function SkeletonRows({ count = 8, columns = 5 }: { count?: number; columns?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, row) => (
        <tr key={row} className="motion-safe:animate-pulse">
          {Array.from({ length: columns }, (_, col) => (
            <Td key={col}>
              <span className="bg-surface-muted block h-3 w-full rounded" />
            </Td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function SkeletonCards({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-2" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <Card key={i} className="p-3.5">
          <div className="motion-safe:animate-pulse space-y-2">
            <span className="bg-surface-muted block h-3.5 w-2/3 rounded" />
            <span className="bg-surface-muted block h-3 w-1/3 rounded" />
          </div>
        </Card>
      ))}
    </div>
  );
}
